import {
  formatDateTimeToLocaleString,
  getDateMinusNMonths,
  getNextOccurrenceWithThreeMonthGracePeriod,
  MembershipValidityPeriod,
} from "@simplyk/common";

import { useLocaleContext } from "@/contexts/LocaleContext";
import { FormInfoTooltip } from "@/forms-design-system/FormTooltip";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { useTranslate } from "@/hooks/useTranslate";

export type ThemedMembershipRenewalInfoProps = {
  validityPeriod: MembershipValidityPeriod;
  renewalDate?: Date;
};

export const ThemedMembershipRenewalInfo = ({ validityPeriod, renewalDate }: ThemedMembershipRenewalInfoProps) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();

  const formattedRenewalDate = renewalDate
    ? formatDateTimeToLocaleString(renewalDate, locale, {
        month: "long",
        day: "numeric",
      })
    : undefined;

  const willRenewInLessThan3Months = !!renewalDate && getDateMinusNMonths(renewalDate, 3) < new Date();

  const formattedRenewalDateIn1Year = renewalDate
    ? formatDateTimeToLocaleString(getNextOccurrenceWithThreeMonthGracePeriod(renewalDate), locale, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : undefined;

  return (
    <FormTypography
      variant="body2"
      vibe="brand"
      sx={{
        display: "flex",
        alignItems: "center",
      }}
    >
      {(() => {
        switch (validityPeriod) {
          case MembershipValidityPeriod.OneMonthAfterPayment:
            return t("ticketing", "rate.item.membershipRenewalInfo.monthly");
          case MembershipValidityPeriod.OneYearAfterPayment:
            return t("ticketing", "rate.item.membershipRenewalInfo.yearly", { date: formattedRenewalDate });
          case MembershipValidityPeriod.AtDate:
            return t("ticketing", "rate.item.membershipRenewalInfo.atDate", { date: formattedRenewalDate });
          default:
            return t("ticketing", "rate.item.membershipRenewalInfo.noExpiration");
        }
      })()}
      {willRenewInLessThan3Months && (
        <FormInfoTooltip
          title={t("ticketing", "rate.item.membershipRenewalInfo.atDateTooltip", {
            date: formattedRenewalDateIn1Year,
          })}
          sx={{ marginLeft: 0.5, color: "inherit" }}
        />
      )}
    </FormTypography>
  );
};
